import { __federation_method_setRemote } from '__federation__';
import { Grid } from '@mui/material';
import { lazy, memo, Suspense, useEffect } from 'react';
import { GCFErrorBoundary } from 'src/components/errors/GCFErrorBoundary';
import { ProgressBox } from 'src/components/progress';
import { FIT_TO_SCREEN_BUTTON_DATA_TEST_ID } from 'src/components/scaleContainer/constants';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { SIDE_NAV_WIDTH } from 'src/constants';
import { useCleanUpDefaultFonts } from 'src/hooks/useCleanUpDefaultFonts';

interface IProps {
  url: string;
}

const LazyGcfPlayer = memo((props: IProps) => {
  useCleanUpDefaultFonts();

  const GcfPlayerScreen = lazy((): any => {
    __federation_method_setRemote('gcf-player', {
      url: () => Promise.resolve(props.url),
      format: 'esm',
      from: 'vite',
    });

    return import('gcf-player/Components');
  });

  useEffect(() => {
    if (!props.url) return;

    const pollForFitToScreenButton = setInterval(() => {
      const fitToScreenButton = document.querySelector<HTMLButtonElement>(
        `[data-testid="${FIT_TO_SCREEN_BUTTON_DATA_TEST_ID}"]`,
      );

      if (fitToScreenButton) {
        fitToScreenButton.click();
        clearInterval(pollForFitToScreenButton);
      }
    }, 100);

    return () => clearInterval(pollForFitToScreenButton);
  }, [props.url]);

  return (
    <GCFErrorBoundary isGcfPlayer>
      <Suspense fallback={<ProgressBox loading />}>
        <ScaleContainer
          enableScaleControls
          containerStyle={{
            width: '100%',
            height: '100%',
          }}
          contentWrapperStyle={{
            padding: 40,
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 8,
          }}
          scaleBarStyles={{
            position: 'absolute',
            left: SIDE_NAV_WIDTH,
            right: '0',
            bottom: '0',
          }}>
          <GcfPlayerScreen
            screenWrapper={ScreenWrapper}
            sx={{
              width: 'fit-content',
              height: 'fit-content',
              '& *': {
                boxSizing: 'unset',
              },
            }}
          />
        </ScaleContainer>
      </Suspense>
    </GCFErrorBoundary>
  );
});

export default LazyGcfPlayer;

const ScreenWrapper = ({ children, width, height }: any) => (
  <Grid container>
    <Grid item width={width} height={height}>
      {children}
    </Grid>
  </Grid>
);
